import React from 'react';
import PropTypes from 'prop-types';
import { truncate } from 'lodash';
import cx from 'classnames';
import Heading from '../Heading';
import StarRating from '../StarRatingNew';
import { useWindowSize } from '../../../hooks';
import FauxImage, { FauxUnavailableImage } from '../FauxImage';
import { PlaceholderText } from '../Placeholder';
import * as InventoryTag from '../inventoryTags/InventoryTag';
import * as AvailabilityTag from '../AvailabilityTags/AvailabilityTag';
import { ExternalLink } from '../../sharedComponents/Link';

export default function InventoryPromoLink({
    className,
    href,
    imagePath,
    mobileImagePath,
    imageAlt,
    imageHeight,
    heading,
    showHeadingOnMobile,
    headingLevel,
    maxHeadingLength,
    copy,
    maxCopyLength,
    location,
    maxLocationLength,
    hasStarRating,
    hasMinimalLayoutForMobile,
    rating,
    reviewCount,
    hasBorder,
    inventoryTags,
    children,
    isLoading,
    availabilityTag,
    isExternalLink,
    ...rest
}) {

    const { width } = useWindowSize();
    // Add padding to the promo if it doesn't have a border
    const doesNotHaveBorder = hasBorder ? '' : 'include-padding'
    const includeBorder = hasBorder ? 'has-a-border' : '';

    function availabilitySwitch() {
        switch(availabilityTag) {
            case 'available':
                return (<AvailabilityTag.Available />);
            case 'partially available':
                return (<AvailabilityTag.PartiallyAvailable />);
            case 'not reservable':
                return (<AvailabilityTag.NotReservable />);
            case 'lottery':
            case "CAMPING_LOTTERY":
                return (<AvailabilityTag.Lottery />);
            case "unavailable":
                return (<AvailabilityTag.Unavailable />);
            case "new":
                return (<AvailabilityTag.New />);
            case "enter dates":
                return (<AvailabilityTag.EnterDates />);
          default:
            return null;
        }
    }

    const promoLinkBody = (
        <div className="sarsa-inventory-promo-link-inner-wrap">
            <div className="sarsa-inventory-promo-link-image-wrap">

                {availabilitySwitch({availabilityTag})}

                {imagePath === undefined ? (
                    <FauxUnavailableImage
                        style={{ minHeight: `${imageHeight}` }} />
                ) : (
                    <FauxImage
                        src={imagePath}
                        altText={imageAlt}
                        style={{ minHeight: `${imageHeight}` }} />
                    )}
            </div>

            <div className={`sarsa-inventory-promo-link-content-wrap ${includeBorder}`}>

                {inventoryTags && (
                    <ul className="rec-inventory-tags" >
                        {inventoryTags.map((inventoryTag, item) => {
                            const PromoInventoryTag = InventoryTag[`Inventory${inventoryTag}`];
                            return (
                                    <PromoInventoryTag hasText key={item} />
                                )
                        })}
                    </ul>
                )}

                <div className="sarsa-inventory-promo-link-text-wrap">
                    {isLoading ? (
                        <PlaceholderText fontSize={16} lines={2} />
                    ) : (
                        <Heading headingLevel={headingLevel} appearance="h3">
                            {truncate(heading, { 'length': maxHeadingLength })}
                        </Heading>
                    )}

                    {location && location.length > 1 && (
                        <React.Fragment>
                            {isLoading ? (
                                <PlaceholderText fontSize={14} lines={2} />
                                ) : (
                                    <p className="mb-half">
                                        {truncate(location, { 'length': maxLocationLength })}
                                    </p>
                            )}
                        </React.Fragment>
                    )}

                    {copy && copy.length > 1 && (
                        <React.Fragment>
                            {isLoading ? (
                                <PlaceholderText fontSize={14} lines={4} />
                                ) : (
                                    <p>
                                        {truncate(copy, { 'length': maxCopyLength })}
                                    </p>
                            )}
                        </React.Fragment>
                    )}
                </div>

                {children}

                {hasStarRating && (
                    <div className="sarsa-inventory-promo-link-content-rating">
                        <StarRating rating={rating} reviewCount={reviewCount} />
                    </div>
                )}
            </div>
        </div>
    );

    // Show a version of the promo with a minimal layout for mobile
    const promoLinkBodyMobile = (
        <div className="sarsa-inventory-promo-minimal-mobile-layout-wrap">
        {showHeadingOnMobile && (
            <React.Fragment>
            {isLoading ? (
                <PlaceholderText fontSize={16} lines={2} />
            ) : (
                <div className="sarsa-inventory-promo-minimal-mobile-heading-wrap">
                    
                        <Heading headingLevel={headingLevel} appearance="h3">
                            {truncate(heading, { 'length': maxHeadingLength })}
                        </Heading>
                    
                </div>
            )}
            </React.Fragment>
            )}

            {imagePath === undefined ? (
                <FauxUnavailableImage
                    style={{ minHeight: '90px' }} />
            ) : (
                <FauxImage
                    src={imagePath}
                    mobileSrc={mobileImagePath}
                    altText={imageAlt}
                    style={{ minHeight: '90px' }} />
                )}
        </div>
    );

    return (
        <React.Fragment>
        {/* If the promo is an external link show this */}
            {isExternalLink ? (
                <React.Fragment>
                    {/* If the width of the viewport is less than 768px and 'hasMinimalLayoutForMobile' */}
                    {width <= 768 && hasMinimalLayoutForMobile ? (
                        <ExternalLink
                            url={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={cx('sarsa-inventory-promo-link-wrap', doesNotHaveBorder, className)}
                            {...rest}>
                                {promoLinkBodyMobile}
                        </ExternalLink>
                    ) : (
                        <ExternalLink
                            url={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={cx('sarsa-inventory-promo-link-wrap', doesNotHaveBorder, className)}
                            {...rest}>
                                {promoLinkBody}
                        </ExternalLink>
                    )}
                </React.Fragment>
            ) : (
                <React.Fragment>
                    {width <= 768 && hasMinimalLayoutForMobile ? (
                        <a
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={cx('sarsa-inventory-promo-link-wrap', doesNotHaveBorder, className)}
                            {...rest}>
                                {promoLinkBodyMobile}
                        </a>
                    ) : (
                        <a
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={cx('sarsa-inventory-promo-link-wrap', doesNotHaveBorder, className)}
                            {...rest}>
                                {promoLinkBody}
                        </a>
                    )}
                </React.Fragment>
            )}
        </React.Fragment>
    );
}

InventoryPromoLink.propTypes = {
    /** Path for the promo image.   */
    imagePath: PropTypes.string,

    /** Path for the mobile version of the promo image.   */
    mobileImagePath: PropTypes.string,

    /** href for the promo.   */
    href: PropTypes.string.isRequired,

    /** Heading for the promo.   */
    heading: PropTypes.string.isRequired,

    /** <h#> for the promo.   */
    headingLevel: PropTypes.number,

    /** Hide the heading on mobile (use this if the background image contains a graphic)*/
    showHeadingOnMobile: PropTypes.bool,

    /** Alt text for the promo image.   */
    imageAlt: PropTypes.string,

    /** Height of the promo image.   */
    imageHeight: PropTypes.string,

    /** Set a character count for the promo heading.   */
    maxHeadingLength: PropTypes.number,

    /** Copy for the promo.   */
    copy: PropTypes.string,

    /** Set a character count for the promo copy.   */
    maxCopyLength: PropTypes.number,

    /** Location Type.   */
    location: PropTypes.string,

    /** Set a character count for the location type.   */
    maxLocationLength: PropTypes.number,

    /** Does the promo include a rating? If so this must be set to "true"   */
    hasStarRating: PropTypes.bool,

    /** Reduce the size of the promo, and only show the image and title.   */
    hasMinimalLayoutForMobile: PropTypes.bool,

    /** Does the promo include Availability Tags?   */
    availabilityTag: PropTypes.oneOf(['', 'available', 'partially available', 'not reservable', 'lottery', 'CAMPING_LOTTERY', 'unavailable', 'new', 'enter dates', 'enter date']),

    /** Rating for the location.   */
    rating: PropTypes.number,

    /** Review count for the location.   */
    reviewCount: PropTypes.number,

    /** Additional className that can be added to the component wrapper.   */
    className: PropTypes.string,

    /** Add a border around the content of the promo.   */
    hasBorder: PropTypes.bool,

    /** Define the inventory tags associated with this promo.   */
    inventoryTags: PropTypes.array,

    /** Display the Placeholder or PlaceholderText component until the item is loaded   */
    isLoading: PropTypes.bool,

    /** Add additional content to the component if necessary.  */
    children: PropTypes.node,

    /** Is the link for the promo pointing to an external source  */
    isExternalLink: PropTypes.bool,

    noticeType: PropTypes.string
};

InventoryPromoLink.defaultProps = {
    headingLevel: 3,
    showHeadingOnMobile: true,
    imageHeight: '140px',
    maxHeadingLength: 43,
    maxCopyLength: 140,
    maxLocationLength: 140,
    hasStarRating: false,
    rating: 0,
    reviewCount: 0,
    hasBorder: true,
    inventoryTags: [],
    isExternalLink: false
};